import {
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import { useMemo, useState } from 'react';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LinkIcon from '@mui/icons-material/Link';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PermissionDecoratorV2 } from '@highimpact/dashboard-ui';
import { useTranslation } from 'react-i18next';

import { useDashboardHost } from '../context/DashboardHostContext';

interface PublicDashboardButtonComponentProps {
  id: number;
  projectId: number;
  siteId: number;
  publicUrl?: string;
  getPublicUrl?: (id: number) => Promise<string>;
}

export function PublicDashboardButtonComponent({
  id,
  publicUrl,
  projectId,
  siteId,
  getPublicUrl,
}: PublicDashboardButtonComponentProps) {
  const host = useDashboardHost();

  const [internalLoadedUrl, setInternallyLoadedUrl] = useState<string | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const resolvedUrl = useMemo(
    () => publicUrl ?? internalLoadedUrl,
    [internalLoadedUrl, publicUrl]
  );

  const { t } = useTranslation('dashboards');

  const handleGenerate = async () => {
    setLoading(true);
    setError(false);

    try {
      const generated = getPublicUrl
        ? await getPublicUrl(id)
        : await host.api.dashboard.createPublicDashboard(id, {
            project_id: projectId,
            site_id: siteId,
          });
      setInternallyLoadedUrl(generated);
      host.showNotification(t('La version partagée a été générée'), 'success');
    } catch {
      setError(true);
      host.showNotification(
        t("La version partagée n'a pas pu être générée"),
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    if (resolvedUrl) {
      navigator.clipboard.writeText(resolvedUrl);
      host.showNotification(t('Lien copié dans le presse-papier'), 'success');
    }
  };

  const handleOpen = () => {
    if (resolvedUrl) {
      window.open(resolvedUrl, '_blank', 'noopener,noreferrer');
    }
  };

  const showUrlSection = Boolean(resolvedUrl);

  return (
    <Stack spacing={2} sx={!showUrlSection ? { pt: 1 } : undefined}>
      <PermissionDecoratorV2
        item={'dashboard_create_shareable_version'}
        project_id={projectId}
        site_id={siteId}
      >
        {!showUrlSection && (
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleGenerate}
            disabled={loading}
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : error ? (
                <ErrorOutlineIcon />
              ) : (
                <LinkIcon />
              )
            }
          >
            {loading
              ? t('Veuillez patienter...')
              : error
              ? t('Réessayer')
              : t('Créer une version partageable')}
          </Button>
        )}
      </PermissionDecoratorV2>
      {showUrlSection && (
        <Stack direction="row" spacing={1} alignItems="center">
          <PermissionDecoratorV2
            item={'dashboard_copy_url_of_the_shareable_version'}
            project_id={projectId}
            site_id={siteId}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                value={resolvedUrl}
                size="small"
                sx={{ minWidth: 120 }}
                InputProps={{ readOnly: true }}
              />
              <IconButton color="secondary" onClick={handleCopy}>
                <ContentCopyIcon />
              </IconButton>
            </Stack>
          </PermissionDecoratorV2>

          <PermissionDecoratorV2
            item={'dashboard_visit_the_shareable_version'}
            project_id={projectId}
            site_id={siteId}
          >
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<OpenInNewIcon />}
              onClick={handleOpen}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('Aller à la version partagée')}
            </Button>
          </PermissionDecoratorV2>
        </Stack>
      )}
    </Stack>
  );
}

export default PublicDashboardButtonComponent;
